import { Node } from '@/classes/Documents/Node.class'
import { Document } from '@/classes/Documents/Document.class'

import { CompareFunction } from '@/helpers/methods'
import { DEFAULT_NODE_SORT, nodesPreSort  } from '@/helpers/node-sort'

export class Folder extends Node {
	ghostNodes = []
	is_search_results = false
	opened = false
	_folders_fetched = false

	constructor(rawNode) {
		super(rawNode)
		this._folders_fetched = rawNode ? Object.prototype.hasOwnProperty.call(rawNode, 'folders') : false
		if (this.is_trash) {
			this.alias = 'trash'
		}
		if (this.is_empty || this.is_root) {
			this._documents = null
		}
	}

	get documents () {
		let result = this._documents
		if (!this.is_empty && !this._documents) {
			result = []
		}
		return result
	}
	set documents (documents) {
		if (Array.isArray(documents)) {
			if (!this._documents) {
				this._documents = []
			}
			documents.forEach(newDocument => {
				const existingDocument = this._documents.find(document => document.id == newDocument.id)
				if (existingDocument) {
					existingDocument.update(newDocument)
				} else {
					const document = new Document(newDocument)
					document.structure = this.structure
					this._documents.push(document)
				}
			})
			this._documents.forEach((existingDocument, existingDocumentIndex) => {
				const newDocument = documents.find(document => document.id == existingDocument.id)
				if (!newDocument) {
					this._documents.splice(existingDocumentIndex, 1)
				}
			})
		}
	}

	get folders() {
		let result = this._folders_fetched ? undefined : []
		if (this._folders?.length) {
			result = [...this._folders].sort(CompareFunction(DEFAULT_NODE_SORT, nodesPreSort))
		}
		return result
	}
	get treeviewFolders() {
		return this.folders
	}
	get foldersFetched() {
		return this._folders_fetched
	}
	set folders (folders) {
		this._folders_fetched = true
		if (Array.isArray(folders)) {
			if (!this._folders) {
				this._folders = []
			}
			folders.forEach(newFolder => {
				const existingFolder = this._folders.find(folder => folder.id == newFolder.id)

				if (existingFolder) {
					existingFolder.update(newFolder)
				} else {
					newFolder.structure = this.structure
					const folder = new Folder(newFolder)
					this._folders.push(folder)
				}
			})
			this._folders.forEach((existingFolder, existingFolderIndex) => {
				const newFolder = folders.find(folder => folder.id == existingFolder.id)
				if (!newFolder) {
					this._folders.splice(existingFolderIndex, 1)
				}
			})
		} else if (folders == null) {
			this._folders = []
		}
	}

	get is_empty () {
		return this._folders?.length === 0 && this._documents?.length === 0
	}

	set is_empty (value) {
		// Do nothing, is_empty is computed
	}

	get has_child () {
		return this._folders?.length > 0 || this._documents?.length > 0
	}

	get is_trash () {
		return this.id == -1
	}

	get children () {
		let result = []
		if (this._folders) {
			result.push(...this._folders)
		}
		if (this._documents) {
			result.push(...this._documents)
		}
		if (result.length == 0) {
			result = null
		}
		return result
	}

	get openedFolders () {
		let result = []
		if (this.opened) {
			const folderIndex = result.findIndex(entry => entry.id == this.id)
			if (folderIndex == -1) {
				result.push(this)
			}
		}
		this._folders
			?.map(folder => folder.openedFolders)
			.flat()
			.forEach(folder => {
				const folderIndex = result.findIndex(entry => entry.id == folder.id)
				if (folderIndex == -1) {
					result.push(folder)
				}
			})
		return result
	}

	markAsOpened () {
		this.opened = true
	}
	markAsClosed () {
		this.opened = false
	}
	findParentNode (nodeIdentifier) {
		let result = null
		if (this.children?.some(child => [child.id, child.hash, child.alias].includes(nodeIdentifier))) {
			result = this
		}
		if (!result && this._folders) {
			for (const folder of this._folders) {
				result = folder.findParentNode(nodeIdentifier)
				if (result) {
					break
				}
			}
		}
		return result
	}
	findNode (nodeIdentifier) {
		let result = null
		if (nodeIdentifier && (this.id == nodeIdentifier || this.hash == nodeIdentifier || this.alias == nodeIdentifier)) {
			result = this
		}
		if (nodeIdentifier && !result) {
			result = this._documents?.find(document => document.id == nodeIdentifier || document.hash == nodeIdentifier)
		}
		if (nodeIdentifier && !result && this._folders) {
			for (const folder of this._folders) {
				result = folder.findNode(nodeIdentifier)
				if (result) {
					break
				}
			}
		}
		if (!result) {
			result = this.findGhostNode(nodeIdentifier)
		}
		return result
	}
	findGhostNode (nodeIdentifier) {
		let result = null
		if (nodeIdentifier && this.structure) {
			result = this.structure.ghostNodes.find(
				ghostNode => ghostNode.id == nodeIdentifier || ghostNode.hash == nodeIdentifier || ghostNode.alias == nodeIdentifier
			)
		}
		return result
	}
	add (rawNode) {
		let result = null
		const rawNodeVendorId = Node.vendorIdOf(rawNode)
		const thisVendorId = Node.vendorIdOf(this)
		if (rawNode && (!thisVendorId || rawNodeVendorId == thisVendorId)) {
			if (Node.isFolderNode(rawNode)) {
				result = this.addFolder(rawNode)
			} else if (Node.isDocumentNode(rawNode)) {
				result = this.addDocument(rawNode)
			}
		}
		this.structure?.tryToAttachGhostNodes()
		return result
	}
	addDocument (rawNode) {
		let result = null
		const rawNodeVendorId = Node.vendorIdOf(rawNode)
		const thisVendorId = Node.vendorIdOf(this)
		if (rawNode && (!thisVendorId || rawNodeVendorId == thisVendorId) && Node.isDocumentNode(rawNode)) {
			if (!this.structure && this.is_root) {
				this.structure = this
			}
			rawNode.structure = this.structure
			rawNode.folder_id = this.id
			if (!this._documents) {
				this._documents = []
			}
			if (!this._documents.map(document => document.id).includes(rawNode.id)) {
				this._documents.push(rawNode instanceof Document ? rawNode : new Document(rawNode))
			}
		}
		return result
	}
	// eslint-disable-next-line
	addFolder (rawNode) {
		let result = null
		const rawNodeVendorId = Node.vendorIdOf(rawNode)
		const thisVendorId = Node.vendorIdOf(this)
		if (rawNode && (!thisVendorId || rawNodeVendorId == thisVendorId) && Node.isFolderNode(rawNode)) {
			if (!this.structure && this.is_root) {
				this.structure = this
			}
			rawNode.structure = this.structure
			rawNode.folder_id = this.id
			if (!this._folders) {
				this._folders = []
			}
			if (!this._folders.map(folder => folder.id).includes(rawNode.id) && !this._folders.map(folder => folder.name).includes(rawNode.name)) {
				this._folders.push(rawNode instanceof Folder ? rawNode : new Folder(rawNode))
			}
		}
		return result
	}
	update (rawNode) {
		const rawNodeVendorId = Node.vendorIdOf(rawNode)
		const thisVendorId = Node.vendorIdOf(this)
		if (rawNode && (!thisVendorId || rawNodeVendorId == thisVendorId)) {
			super.update(rawNode)
		}
		this.structure?.tryToAttachGhostNodes()
		return this
	}
	remove (rawNode) {
		let result = false
		let node = rawNode instanceof Node ? rawNode : null
		if (!node && Node.isFolderNode(rawNode)) {
			node = new Folder(rawNode)
		} else if (!node && Node.isDocumentNode(rawNode)) {
			node = new Document(rawNode)
		}
		if (node?.is_folder) {
			result = this.removeFolder(node)
		} else if (node?.is_document) {
			result = this.removeDocument(node)
		}
		return result
	}
	removeFolder (node) {
		let result = false
		if (this._folders) {
			const nodeIndex = this._folders.findIndex(subfolder => subfolder.id == node.id)
			if (nodeIndex != -1) {
				this._folders.splice(nodeIndex, 1)
				result = true
			} else {
				for (const subfolder of this._folders) {
					result = subfolder.remove(node)
					if (result) {
						break
					}
				}
			}
		}
		if (result) {
			this.updateCounter()
		}
		return result
	}
	removeDocument (node) {
		let result = false
		if (this._documents) {
			const nodeIndex = this._documents.findIndex(document => document.id == node.id)
			if (nodeIndex != -1) {
				this._documents.splice(nodeIndex, 1)
				result = true
			}
		}
		if (result) {
			this.updateCounter()
		}
		return result
	}
	resetSort (sortFields = DEFAULT_NODE_SORT) {
		this.deepSort(sortFields)
	}
	deepSort (sortFields) {
		if (this._folders) {
			this._folders.sort(CompareFunction(sortFields, nodesPreSort))
			this._folders.forEach(folder => folder.deepSort(sortFields))
		}
	}
	clearChildren () {
		if (this.children) {
			this.children.forEach(child => this.remove(child))
		}
	}
	updateCounter () {
		this.counter = this.children?.map(child => child.counter).reduce((sumResult, counter) => sumResult + counter, 0)
	}
	removeCounter () {
		this.children?.forEach(child => child.removeCounter())
		this.counter = 0
	}
	toString (indentation = '') {
		let result = `${indentation}${this.name}`
		this._folders?.forEach(subfolder => {
			result += '\n'
			result += subfolder.toString(indentation + '\t')
		})
		this._documents?.forEach(subdocument => {
			result += '\n'
			result += subdocument.toString(indentation + '\t')
		})
		return result
	}
}
